import React from 'react';
import {
  BookOpen, Clock, CheckCircle2, Sparkles, X,
  CreditCard, Lock, Play, FileText, HelpCircle, Layers, Award
} from 'lucide-react';
import { normalizeCourse, localCourseResumen } from '../../utils/courseStructure';
import '../../styles/CursosVortex.css';

export const CourseDetailModal = ({
  course,
  isOpen,
  onClose,
  isSubscribed,
  isAuthenticated,
  onEnroll,
  onStartCourse
}) => {
  if (!isOpen || !course) return null;

  const normCourse = normalizeCourse(course);
  const resumen = course.resumen || localCourseResumen(course);
  // Si el contenido real vive en Firestore (vortexAcademyAreas), renderiza ese temario.
  const areaTemas = course.areaTemas || [];
  const temas = areaTemas.length > 0
    ? areaTemas.map((t, i) => ({ id: t.id, title: t.title, lessons: [], area: true, lecciones: t.lecciones, miniExamenes: t.miniExamenes }))
    : (normCourse?.temas || []);
  const temasConFinal = temas.filter((t) => (t.examen_final?.questions?.length || 0) > 0);

  return (
    <div className="vx-modal-overlay" onClick={onClose}>
      <div className="vx-modal-card" onClick={(e) => e.stopPropagation()}>

        {/* ── Header con Imagen ── */}
        <div className="vx-modal-cover">
          <img src={course.thumbnail} alt={course.title} />
          <div className="vx-modal-cover-gradient" />

          <button className="btn-vx-modal-close" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>

          <div className="vx-modal-badges">
            <span className="vx-modal-badge-tag">{course.category}</span>
            <span className="vx-modal-badge-tag period">
              🗓️ {course.periodName || 'Septiembre - Noviembre'}
            </span>
          </div>

          <div className="vx-modal-cover-title">
            <h2>{course.title}</h2>
          </div>
        </div>

        {/* ── Cuerpo Modal ── */}
        <div className="vx-modal-body">

          {/* Métricas */}
          <div className="vx-modal-metrics">
            <div className="vx-modal-metric-item">
              <div className="vx-modal-metric-icon">
                <BookOpen className="w-5 h-5" />
              </div>
              <div>
                <span className="vx-modal-metric-label">Contenido</span>
                <span className="vx-modal-metric-val">
                  {resumen.temas} Temas · {resumen.lecciones} Lecciones
                </span>
              </div>
            </div>

            <div className="vx-modal-metric-item">
              <div className="vx-modal-metric-icon" style={{ color: 'var(--gold-light)' }}>
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <span className="vx-modal-metric-label">Duración Total</span>
                <span className="vx-modal-metric-val">{course.duration_minutes} Minutos</span>
              </div>
            </div>

            <div className="vx-modal-metric-item">
              <div className="vx-modal-metric-icon" style={{ color: '#fbbf24', background: 'rgba(245, 158, 11, 0.1)' }}>
                <CreditCard className="w-5 h-5" />
              </div>
              <div>
                <span className="vx-modal-metric-label">Inversión</span>
                <span className="vx-modal-metric-val" style={{ color: '#fbbf24', fontFamily: 'var(--font-tactical)' }}>
                  $250 <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 400 }}>– $1,900 MXN</span>
                </span>
              </div>
            </div>

            <div className="vx-modal-metric-item">
              <div className="vx-modal-metric-icon" style={{ color: '#34d399', background: 'rgba(16, 185, 129, 0.1)' }}>
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <span className="vx-modal-metric-label">Acceso</span>
                <span className="vx-modal-metric-val">
                  {isSubscribed ? '✅ Desbloqueado' : '🔒 Requiere Licencia'}
                </span>
              </div>
            </div>
          </div>

          {/* Descripción */}
          <div>
            <h3 className="vx-modal-sec-title">📋 Descripción y Propósito</h3>
            <p className="vx-modal-desc-box">{course.description}</p>
          </div>

          {/* Temario */}
          <div>
            <h3 className="vx-modal-sec-title">
              <span>📚 Temario Completo del Curso ({resumen.temas} temas · {resumen.miniExamenes} mini exámenes · {resumen.examenes} exámenes)</span>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace', fontWeight: 400 }}>
                {course.resumen?.source === 'manual' ? 'Conteo fijado por el administrador' : 'Conteo verificado de Vortex Academy'}
              </span>
            </h3>

            <div className="vx-modal-lessons-list">
              {temas.length > 0 ? (
                temas.map((tema, tIdx) => (
                  <div key={tema.id || tIdx} className="vx-modal-lesson-card" style={{ borderColor: 'rgba(197,160,89,0.35)' }}>
                    <div className="vx-modal-lesson-card-head">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="vx-modal-lesson-num" style={{ background: 'rgba(197,160,89,0.2)', color: 'var(--gold-matte)' }}>
                          {tIdx + 1}
                        </span>
                        <h4 style={{ color: 'var(--gold-matte)' }}>{tema.title}</h4>
                      </div>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                        <Layers className="w-3.5 h-3.5" style={{ color: 'var(--gold-matte)' }} />
                        <span>
                          {tema.area
                            ? `${tema.lecciones || 0} lecciones · ${tema.miniExamenes || 0} mini exámenes`
                            : `${tema.lessons?.length || 0} lecciones · ${tema.lessons?.reduce((a, l) => a + (l.examenes?.length || 0), 0) || 0} exámenes`}
                        </span>
                      </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '4px 0' }}>
                      {tema.area ? (
                        <div style={{
                          padding: '10px 12px', borderRadius: '8px', background: 'rgba(16,185,129,0.06)',
                          border: '1px solid rgba(16,185,129,0.2)', fontSize: '0.72rem', color: 'var(--text-secondary)', lineHeight: 1.6,
                        }}>
                          Contenido disponible en el <strong style={{ color: '#34d399' }}>Lector de Temas</strong> de Vortex
                          Academy: {tema.lecciones || 0} lecciones de lectura y {tema.miniExamenes || 0} mini exámenes evaluativos.
                        </div>
                      ) : (
                      (tema.lessons || []).map((lesson, li) => (
                        <div key={lesson.id || li} style={{
                          padding: '10px 12px', borderRadius: '8px', background: 'rgba(10,26,42,0.6)',
                          border: '1px solid rgba(197,160,89,0.12)', display: 'flex', flexDirection: 'column', gap: '6px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                            <span style={{ fontSize: '0.76rem', color: '#fff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Play className="w-3.5 h-3.5" style={{ color: 'var(--gold-matte)' }} />
                              <span>{lesson.title}</span>
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'monospace', flexShrink: 0 }}>
                              <Clock className="w-3 h-3" style={{ color: 'var(--gold-matte)' }} />
                              {Math.round((lesson.duration_seconds || 300) / 60)} min
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                            {lesson.pdf_resources?.length > 0 && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <FileText className="w-3 h-3" style={{ color: '#f87171' }} />
                                {lesson.pdf_resources.length} PDF
                              </span>
                            )}
                            {lesson.quiz?.length > 0 && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <HelpCircle className="w-3 h-3" style={{ color: '#fbbf24' }} />
                                {lesson.quiz.length} Preguntas de Quiz
                              </span>
                            )}
                            {lesson.quizId && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Sparkles className="w-3 h-3" style={{ color: 'var(--gold-light)' }} />
                                Quiz Vortex Academy
                              </span>
                            )}
                          </div>

                          {/* Exámenes de la lección */}
                          {(lesson.examenes || []).length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '2px' }}>
                              {(lesson.examenes || []).map((exam, ei) => (
                                <div key={exam.id || ei} style={{
                                  padding: '8px 10px', borderRadius: '8px', background: 'rgba(245,158,11,0.06)',
                                  border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'center', gap: '8px'
                                }}>
                                  <Award className="w-3.5 h-3.5" style={{ color: '#f59e0b' }} />
                                  <span style={{ fontSize: '0.72rem', color: '#f59e0b', fontWeight: 700 }}>{exam.title}</span>
                                  <span style={{ marginLeft: 'auto', fontSize: '0.66rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                                    {exam.questions?.length || 0} preguntas
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  No hay temas registrados para este curso.
                </p>
              )}
            </div>
          </div>

          {/* Exámenes finales por tema */}
          {temasConFinal.length > 0 && (
            <div style={{
              padding: '1rem', borderRadius: '12px', background: 'rgba(16,185,129,0.06)',
              border: '1px solid rgba(16,185,129,0.25)', display: 'flex', alignItems: 'center', gap: '12px'
            }}>
              <div style={{ padding: '10px', borderRadius: '8px', background: 'rgba(16,185,129,0.12)', color: '#34d399' }}>
                <Award className="w-6 h-6" />
              </div>
              <div>
                <h4 style={{ fontFamily: 'var(--font-tactical)', fontSize: '0.78rem', color: '#34d399', margin: '0 0 2px' }}>
                  Examen Final por Tema · {temasConFinal.length} tema(s)
                </h4>
                <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                  Cada tema tiene su propio examen final que se libera al aprobar los exámenes de sus lecciones.
                  Apruebas con 70% para completar el tema y sumar a tu certificado.
                </p>
              </div>
            </div>
          )}

          {/* Garantía */}
          <div style={{
            padding: '1rem',
            borderRadius: '12px',
            background: 'rgba(10, 26, 42, 0.8)',
            border: '1px solid rgba(197, 160, 89, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{ padding: '10px', borderRadius: '8px', background: 'rgba(197, 160, 89, 0.1)', color: 'var(--gold-matte)' }}>
              <Lock className="w-6 h-6" />
            </div>
            <div>
              <h4 style={{ fontFamily: 'var(--font-tactical)', fontSize: '0.78rem', color: '#fff', margin: '0 0 2px' }}>
                Inscripción por Periodo Bimestral
              </h4>
              <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                Acceso completo a todos los temas, quices evaluativos, audios y temarios en PDF mediante pago seguro con Stripe.
              </p>
            </div>
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="vx-modal-footer-bar">
          <div>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'block', fontFamily: 'monospace' }}>Vigencia disponible desde:</span>
            <span className="vx-modal-price-tag">$250 MXN</span>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace', marginLeft: '4px' }}>mensual · hasta $1,900 anual</span>
          </div>

          <div className="vx-modal-footer-btns">
            <button className="btn-card-resumen" onClick={onClose} style={{ padding: '10px 20px' }}>
              Cerrar
            </button>

            {isSubscribed ? (
              <button
                className="btn-card-enter"
                onClick={() => { onClose(); onStartCourse(course); }}
                style={{ padding: '10px 22px' }}
              >
                <Play className="w-4 h-4" />
                <span>Ingresar al Curso</span>
              </button>
            ) : (
              <button
                className="btn-card-buy"
                onClick={() => { onClose(); onEnroll(course); }}
                style={{ padding: '10px 22px' }}
              >
                <CreditCard className="w-4 h-4" />
                <span>{isAuthenticated ? 'Elegir Vigencia y Adquirir' : 'Iniciar Sesión para Comprar'}</span>
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};
